/**
 * DIAG-DISCOUNT v5 (record = intent · apply at settlement) — 1:1 with the
 * FROZEN §1.1/§1.2 API CONTRACT
 * (state/epics/DIAG-DISCOUNT/plan/build-plan-v5.md). Binds to the CONTRACT,
 * not to built backend code — the backend is being written in parallel.
 *
 * An ADMIN records one amount + reason against an admitted stay; nothing
 * moves. A RECEPTIONIST settles later and the SERVER allocates the recorded
 * amount pro-rata — the settle request carries no waiver input at all (that
 * wiring is `creditsApi.settleCredits`, out of scope here).
 *
 * Both request schemas are `extra="forbid"` server-side — NEVER echo a
 * derived/read-only field (e.g. `remaining_paise`, `waiver_id`) back onto a
 * write body, that is a 422. Money is integer paise on the wire.
 *
 * Each function returns `{ success, data?, error?, code?, ...extras }`; on
 * failure `code` + every extra from `detail` pass through untouched (e.g.
 * 409 `WAIVER_ALREADY_ACTIVE{waiver_id,recorded_paise,remaining_paise}`, 422
 * `WAIVER_EXCEEDS_OUTSTANDING{max_recordable_paise}`, 422
 * `REVERSAL_EXCEEDS_REMAINING{remaining_paise}`, 403 with
 * `required_permission`) so the record/reverse dialog can branch on the
 * machine code rather than parse a message string.
 */

import apiClient from './apiClient';

const BASE = '/api/organisation/admissions';
const enc = encodeURIComponent;

/** Same detail-shape translation the admission charges/credits API uses. */
function parseError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    const { code, message, ...extras } = detail;
    return { error: message || fallback, code, ...extras };
  }
  if (typeof detail === 'string') return { error: detail };
  switch (error?.response?.status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission to record a diagnostic waiver.' };
    case 404: return { error: 'Admission not found.' };
    case 422: return { error: fallback, code: 'VALIDATION_ERROR' };
    case 503: return { error: 'Admissions are temporarily unavailable. Try again shortly.', code: 'IPD_TABLE_UNAVAILABLE' };
    default:  return { error: fallback };
  }
}

/**
 * §1.1 POST /admissions/{id}/diagnostics-waiver — record a diagnostic waiver
 * against an admitted stay. Body, `extra="forbid"`: exactly
 * `{ amount_paise, reason, idempotency_key }`. A 201 (created) and a 200
 * (idempotent replay) are BOTH success.
 *
 * Response (passed through as `data`, untouched):
 * `{ admission_id, ip_number, waiver_id, recorded_paise, reversed_paise,
 *    applied_paise, remaining_paise, reason, recorded_at,
 *    recorded_by_member_id, recorded_by_name, unsettled_lab_paise, replayed }`
 * — `remaining_paise` is DERIVED server-side; never sent back on a write.
 */
export const recordDiagnosticWaiver = async (admissionId, { amount_paise, reason, idempotency_key } = {}) => {
  try {
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/diagnostics-waiver`, {
      amount_paise,
      reason,
      idempotency_key,
    });
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.error('[DiagWaiverAPI] recordDiagnosticWaiver error:', error);
    return { success: false, ...parseError(error, 'Failed to record the diagnostic waiver.') };
  }
};

/**
 * §1.2 POST /admissions/{id}/diagnostics-waiver/reverse — reverse the
 * UNAPPLIED part of the active waiver (`n <= remaining`). Body,
 * `extra="forbid"`: exactly `{ amount_paise, reason, idempotency_key }`.
 * `amount_paise: null` means "all remaining" — a real, distinct request from
 * `amount_paise: 0`. When the caller omits `amount_paise` entirely it
 * defaults to `null` (all-remaining) rather than being dropped, so the
 * server always receives the field it declares.
 *
 * Response (passed through as `data`, untouched) = the same money block as
 * record, plus `{ reversal_id, reversed_at, reversed_by_member_id,
 * reversed_by_name, replayed }`.
 */
export const reverseDiagnosticWaiver = async (admissionId, { amount_paise, reason, idempotency_key } = {}) => {
  try {
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/diagnostics-waiver/reverse`, {
      amount_paise: amount_paise === undefined ? null : amount_paise,
      reason,
      idempotency_key,
    });
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.error('[DiagWaiverAPI] reverseDiagnosticWaiver error:', error);
    return { success: false, ...parseError(error, 'Failed to reverse the diagnostic waiver.') };
  }
};

const diagWaiverApi = { recordDiagnosticWaiver, reverseDiagnosticWaiver };
export default diagWaiverApi;
